import React from 'react';
import { HStack, Badge, Tooltip, Icon } from '@chakra-ui/react';
import * as FiIcons from 'react-icons/fi';
import { useCrmSettings } from '../../contexts/CrmSettingsContext';

/**
 * Display contact types as badges
 * 
 * Features:
 * - Shows primary type prominently
 * - Other types as smaller badges
 * - Color-coded based on admin config
 * - Icons if configured
 * - Tooltip with description
 */
export default function ContactTypeBadges({ 
  contactTypes = [], 
  primaryContactType = null,
  size = 'sm',
  maxDisplay = 3,
  showIcons = false,
}) {
  const { getContactType } = useCrmSettings();

  if (!contactTypes || contactTypes.length === 0) {
    return (
      <Badge size={size} colorScheme="gray">
        Unknown
      </Badge>
    );
  }

  // Ensure primary is first. Only honor primaryContactType when it is actually
  // one of the contact's types — a stale primary (e.g. left behind by an older
  // bulk update that only replaced contact_types) must not render as a phantom
  // badge that isn't in the data.
  const primary = (primaryContactType && contactTypes.includes(primaryContactType))
    ? primaryContactType
    : contactTypes[0];
  const orderedTypes = [primary, ...contactTypes.filter(t => t !== primary)];

  // Limit display
  const displayTypes = orderedTypes.slice(0, maxDisplay);
  const remaining = contactTypes.length - maxDisplay;

  return (
    <HStack spacing={1} flexWrap="wrap">
      {displayTypes.map((typeValue, index) => {
        const typeConfig = getContactType(typeValue);
        const isPrimary = index === 0;
        
        if (!typeConfig) {
          return (
            <Badge key={typeValue} size={size} colorScheme="gray">
              {typeValue}
            </Badge>
          );
        }

        const IconComponent = showIcons && typeConfig.icon && FiIcons[typeConfig.icon] 
          ? FiIcons[typeConfig.icon] 
          : null;

        return (
          <Tooltip 
            key={typeValue} 
            label={typeConfig.description || typeConfig.label}
            hasArrow
          >
            <Badge
              size={isPrimary ? size : 'xs'}
              colorScheme={typeConfig.color}
              fontWeight={isPrimary ? 'bold' : 'normal'}
              display="flex"
              alignItems="center"
              gap={1}
            >
              {IconComponent && <Icon as={IconComponent} boxSize={2} />}
              {typeConfig.label}
            </Badge>
          </Tooltip>
        );
      })}
      
      {remaining > 0 && (
        <Badge size="xs" colorScheme="gray">
          +{remaining}
        </Badge>
      )}
    </HStack>
  );
}

